// src/pages/Quiz.js

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Container, Typography, Box, Button, Dialog, DialogActions, DialogContent, 
    DialogContentText, DialogTitle, CircularProgress
} from '@mui/material';
import QuizCard from '../components/QuizCard';
import EndScreen from '../components/EndScreen';
import QuizNavigation from '../components/QuizNavigation';
import { saveExamResult, saveQuizState, loadQuizState, clearQuizState } from '../utils/quizUtils';

const Quiz = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // This state starts as null. All initialization is handled in the first useEffect hook.
    const [quizState, setQuizState] = useState(null);

    const [quizFinished, setQuizFinished] = useState(false);
    const [finalResults, setFinalResults] = useState({ score: 0, answers: [] });
    const [openSubmitDialog, setOpenSubmitDialog] = useState(false);

    // --- LIFECYCLE EFFECTS ---

    // This single useEffect runs ONLY ONCE on mount and handles all initialization.
    useEffect(() => {
        // Priority 1: A new quiz start from the QuizSetup page.
        if (location.state && location.state.questions) {
            clearQuizState(); // Clear any old saved quiz from localStorage.
            const newQuiz = {
                questions: location.state.questions,
                title: location.state.title,
                timeLimitMinutes: location.state.timeLimitMinutes,
                sourceFiles: location.state.sourceFiles, // For the "Retake" feature
                isLiveExam: location.state.isLiveExam || false,
                liveExamId: location.state.liveExamId,
                revealedAnswers: Array(location.state.questions.length).fill(false),
                currentQuestionIndex: 0,
                userAnswers: Array(location.state.questions.length).fill(null),
                timeLeft: location.state.timeLimitMinutes ? location.state.timeLimitMinutes * 60 : null,
            };
            setQuizState(newQuiz);

            // Clear the navigation state to prevent issues on refresh
            navigate(location.pathname, { replace: true, state: {} });
            return;
        }
        
        // Priority 2: Resume a saved quiz from a previous session found in localStorage.
        const savedState = loadQuizState();
        if (savedState) {
            setQuizState(savedState);
            return;
        }

        // Priority 3: No new or saved quiz found, so redirect to the homepage.
        navigate('/');

    }, []); // Empty dependency array ensures this runs only ONCE.

    // This effect saves the current quiz progress to localStorage whenever the state changes.
    useEffect(() => {
        if (quizState && !quizFinished) {
            saveQuizState(quizState);
        }
    }, [quizState, quizFinished]);

    // The core function for ending the quiz, calculating score, and saving the result.
    const finishQuiz = () => {
        if (!quizState || !quizState.questions || quizFinished) return; // Prevent multiple finishes
        
        let finalScore = 0;
        // Helper to strip HTML for text comparison
        const getPlainText = (data) => (typeof data === 'string' ? data : (data?.text || '')).replace(/<[^>]*>?/gm, '');
        
        const compiledAnswers = quizState.questions.map((question, index) => {
            const userAnswer = quizState.userAnswers[index];
            const isCorrect = userAnswer !== null && getPlainText(userAnswer) === getPlainText(question.answer);
            if (isCorrect) finalScore++;
            return { ...question, selectedOption: userAnswer, isCorrect };
        });

        setFinalResults({ score: finalScore, answers: compiledAnswers });
        
        // Save exam result to local storage (no user context required)
        saveExamResult({
            title: quizState.title,
            score: finalScore,
            totalQuestions: quizState.questions.length,
            dateCompleted: new Date().toISOString(),
            sourceFiles: quizState.sourceFiles,
            userAnswers: compiledAnswers,
        });
        
        clearQuizState(); // Clean up in-progress state from localStorage.
        setQuizFinished(true);
    };

    // Timer Logic
    useEffect(() => {
        if (!quizState || quizState.timeLeft === null || quizFinished) return;
        
        const timerId = setInterval(() => {
            setQuizState(prevState => {
                if (!prevState) return null;
                // If time runs out, trigger finish (handled by next effect)
                if (prevState.timeLeft <= 0) return prevState;
                return { ...prevState, timeLeft: prevState.timeLeft - 1 };
            });
        }, 1000);
        
        return () => clearInterval(timerId);
    }, [quizState?.timeLeft, quizFinished]);

    // Watcher to auto-submit when time expires
    useEffect(() => {
        if (quizState && quizState.timeLeft !== null && quizState.timeLeft <= 0 && !quizFinished) {
            finishQuiz();
        }
    }, [quizState?.timeLeft, quizFinished]);

    // --- EVENT HANDLERS ---
    const handleAnswerSelect = (selectedOption) => setQuizState(prevState => {
        const newAnswers = [...prevState.userAnswers];
        newAnswers[prevState.currentQuestionIndex] = selectedOption;
        return { ...prevState, userAnswers: newAnswers };
    });

    const moveToNextQuestion = () => setQuizState(prevState => {
        // Find next unanswered question or loop to next index
        let nextIndex = -1;
        // Simple logic: just go to next index, wrapping around
        const potentialNext = (prevState.currentQuestionIndex + 1) % prevState.questions.length;
        
        return { ...prevState, currentQuestionIndex: potentialNext };
    });

    const handleCheckAnswer = () => setQuizState(prevState => {
        const newRevealed = [...prevState.revealedAnswers];
        newRevealed[prevState.currentQuestionIndex] = true;
        return { ...prevState, revealedAnswers: newRevealed };
    });

    const handleGoToQuestion = (index) => setQuizState(prevState => ({ ...prevState, currentQuestionIndex: index }));
    const handleSkip = () => moveToNextQuestion();
    const handleOpenSubmitDialog = () => setOpenSubmitDialog(true);
    const handleCloseSubmitDialog = () => setOpenSubmitDialog(false);
    const handleConfirmSubmit = () => { handleCloseSubmitDialog(); finishQuiz(); };

    const restartQuiz = () => {
        navigate('/');
    };

    const formatTime = (seconds) => {
        if (seconds === null) return '';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    // --- RENDER LOGIC ---
    if (!quizState) {
        return (
            <Container sx={{ textAlign: 'center', mt: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading Quiz...</Typography>
            </Container>
        );
    }
    
    const allQuestionsAnswered = quizState.userAnswers.every(answer => answer !== null);
    
    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" component="h1">{quizState.title}</Typography>
                {quizState.timeLeft !== null && !quizFinished && (
                    <Typography variant="h5" component="div" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                        Time: {formatTime(quizState.timeLeft)}
                    </Typography>
                )}
            </Box>

            {quizFinished ? (
                <EndScreen
                    score={finalResults.score}
                    totalQuestions={quizState.questions.length}
                    userAnswers={finalResults.answers}
                    onRestart={restartQuiz}
                />
            ) : (
                <>
                    <QuizCard
                        question={quizState.questions[quizState.currentQuestionIndex]}
                        questionNumber={quizState.currentQuestionIndex + 1}
                        totalQuestions={quizState.questions.length}
                        selectedValue={quizState.userAnswers[quizState.currentQuestionIndex]}
                        isRevealed={quizState.revealedAnswers[quizState.currentQuestionIndex]}
                        allQuestionsAnswered={allQuestionsAnswered}
                        onAnswerSelect={handleAnswerSelect}
                        onCheckAnswer={handleCheckAnswer}
                        onSkip={handleSkip}
                        onNextQuestion={moveToNextQuestion}
                        onFinishQuiz={finishQuiz}
                    />
                    <QuizNavigation
                        questions={quizState.questions}
                        userAnswers={quizState.userAnswers}
                        currentQuestionIndex={quizState.currentQuestionIndex}
                        onQuestionSelect={handleGoToQuestion}
                    />
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                        <Button variant="contained" color="error" size="large" onClick={handleOpenSubmitDialog}>
                            Submit Quiz
                        </Button>
                    </Box>
                </>
            )}

            <Dialog open={openSubmitDialog} onClose={handleCloseSubmitDialog}>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to end the quiz? Any unanswered questions will be marked as incorrect.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSubmitDialog}>Cancel</Button>
                    <Button onClick={handleConfirmSubmit} color="error" autoFocus>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Quiz;
